import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { searchService } from '../../services/search';
import type { LocationSearchResult } from '../../types/search';

/** The place picked: what the stage form sends as `venue_id`. */
export interface PickedVenue {
  id: string;
  name: string;
  city?: string | null;
}

/**
 * Where a stage is held — a gym or a crag on DivePeaks, found by name.
 *
 * Naming one hands the stage to that place's staff, with the roles they hold
 * there, and makes its climbing area and its setters the stage's own; the
 * help text says so, because it is the one field in this form that grants
 * somebody something. Optional: a stage held somewhere we do not know is left
 * blank, and the circuit's own staff run it alone.
 */
export function VenuePicker({
  value,
  onChange,
}: {
  value: PickedVenue | null;
  onChange: (venue: PickedVenue | null) => void;
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Only the latest search may fill the list: a slow answer to "ro" must not
  // land after the answer to "rockup".
  const latest = useRef(0);

  const search = async (term: string) => {
    const ticket = ++latest.current;
    if (term.trim().length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await searchService.search(term);
      if (ticket !== latest.current) return;
      setOptions(
        res.data
          .filter((item) => item.type === 'location' && (item.subtype === 'gym' || item.subtype === 'crag'))
          .map((item) => {
            const place = item.attributes as LocationSearchResult;
            return { value: item.id, label: place.city ? `${place.name} · ${place.city}` : place.name, place };
          })
      );
    } catch {
      if (ticket === latest.current) setOptions([]);
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="stage-venue">{t('gymAdmin.stages.venue')}</Label>
      {value ? (
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm" data-testid="stage-venue">
          <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {value.name}
            {value.city && <span className="text-muted-foreground"> · {value.city}</span>}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={() => onChange(null)}
            aria-label={t('gymAdmin.stages.venueClear')}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <Combobox
          id="stage-venue"
          value=""
          options={options}
          loading={loading}
          placeholder={t('gymAdmin.stages.venuePlaceholder')}
          onInputChange={search}
          onSelect={(option) => {
            if (!option) return;
            const place = option.place as LocationSearchResult;
            onChange({ id: option.value, name: place.name, city: place.city });
            setOptions([]);
          }}
        />
      )}
      <p className="text-xs text-muted-foreground">{t('gymAdmin.stages.venueHelp')}</p>
    </div>
  );
}
